/**
 * The safety register entries shown to the client, on the client portal's Safety page (MB's decision 1023;
 * docs/specs/safety-register.md §10; help `cloud.safety-documents`). Read only: each record the operator's tenant
 * admins and managers chose with "Show to client" (accidents, incidents, near misses, landholder complaints, height
 * breaches), newest first, with what a client may read of it (`my_safety_register`; the domain's
 * `clientRegisterRecords`). Preview as client shows exactly this. Nothing is listed until someone chooses.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  clientRegisterRecords,
  jspPrintedDate,
  type ClientRegisterRecord,
  type ClientRegisterRow,
} from '@teslyn/domain';
import { missingView, plainPortalError, type PortalProject } from '../portal/api.ts';

/** The project's records shown to its client; none while Teslyn Cloud has not got the choice (20261104230000). */
export async function loadClientRegister(
  sb: SupabaseClient,
  projectId: string,
): Promise<ClientRegisterRecord[]> {
  const { data, error } = (await sb.rpc('my_safety_register', { p_project: projectId })) as {
    data: unknown;
    error: { code?: string; message: string } | null;
  };
  if (missingView(error) || /my_safety_register/.test(error?.message ?? '')) return [];
  if (error) throw new Error(plainPortalError(error));
  return clientRegisterRecords((Array.isArray(data) ? data : []) as ClientRegisterRow[]);
}

const printed = (iso: string | null): string => (iso ? jspPrintedDate(iso.slice(0, 10)) : '');

export function ClientRegister({
  sb,
  project,
  onLoaded,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  /** How many records are shown (the Safety page says when nothing at all is shared). */
  readonly onLoaded?: (n: number) => void;
}): ReactElement | null {
  const [records, setRecords] = useState<ClientRegisterRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadClientRegister(sb, project.id)
      .then((r) => {
        if (!live) return;
        setRecords(r);
        onLoaded?.(r.length);
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per project
  }, [sb, project.id]);

  if (error)
    return (
      <p className="notice notice--fail" role="alert">
        The safety register entries could not be read. {error}
      </p>
    );
  if (!records || records.length === 0) return null;
  return (
    <section className="stack" aria-label="Safety register">
      <h2 className="heading">Safety register</h2>
      <p className="caption muted">The safety register entries of this survey shared with you.</p>
      <ul className="stack" aria-label="Safety register entries">
        {records.map((r) => (
          <li key={`${r.source}:${r.id}`} className="panel stack" aria-label={r.topic}>
            <div className="row row--wrap">
              <span className="chip">{r.kind_words}</span>
              <span className="body">{r.topic}</span>
              <span className="caption muted data">
                {printed(r.occurred_on)} {r.occurred_at}
              </span>
              <span className={r.closed_at ? 'chip chip--pass' : 'chip chip--warn'}>
                {r.closed_at
                  ? `${r.source === 'height_breach' ? 'Reason recorded' : 'Closed'} ${printed(r.closed_at)}`
                  : 'Open'}
              </span>
            </div>
            <dl className="kv safety-details">
              {r.lines.map(([k, v]) => (
                <div key={k} className="safety-details__row">
                  <dt className="label">{k}</dt>
                  <dd className="body safety-details__text">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="caption muted">Shared {printed(r.shown_at)}.</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
